import { useMemo } from 'react';
import { useOptionalFieldDefinitions } from '../contexts/FieldDefinitionsContext';
import {
  buildProCardColumns,
  buildProPickerFields,
  resolveFieldAliases,
} from '../components/proCardColumns';

/**
 * The Search Results PRO grid described as a layout DESTINATION — what columns it
 * has, what those ids mean, and what it could still add.
 *
 * Deliberately NEUTRAL: no user's current extra columns, because this answers
 * "could this View land here", and a View brings its own extras with it. The
 * grid's own live destination (SearchResults' `proColumns` / `availableProFields`)
 * is a different object for a different job.
 *
 * Shared by the two places that must agree about it, or a View is offered in the
 * picker and then refused on arrival: the picker's compatibility filter
 * (useCompatibleSearchViews) and the `?view=<id>` apply path.
 */
export interface ProSearchDestination {
  columnIds: string[];
  additionalFields: string[];
  availableFields: { fieldname: string }[];
  canAddFields: boolean;
  fieldAliases: Readonly<Record<string, readonly string[]>>;
  /** False while the registry is still loading — nothing can be judged yet. */
  ready: boolean;
}

export function useProSearchDestination(): ProSearchDestination {
  const fieldDefinitions = useOptionalFieldDefinitions();
  return useMemo(() => {
    const columns = buildProCardColumns(() => {}) as ReadonlyArray<{ id?: string; accessorKey?: string }>;
    return {
      columnIds: columns.map((c) => c.id ?? c.accessorKey).filter((id): id is string => !!id),
      additionalFields: [],
      availableFields: buildProPickerFields({
        columns: columns as ReadonlyArray<{ id?: string; accessorKey?: string; header?: unknown }>,
        fieldDefinitions,
        additionalFields: [],
      }),
      canAddFields: true,
      fieldAliases: resolveFieldAliases(columns, fieldDefinitions),
      // The registry is what tells us which fields the grid could ADD and what its
      // own columns mean, so with none of it loaded every verdict would be a false
      // refusal.
      ready: fieldDefinitions.length > 0,
    };
  }, [fieldDefinitions]);
}
